import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'src/utils/Hooks';
import { trackGenerationStarted } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/tracking';
import {
  GetCurrentContentRequest,
  GetContentGenerationStatusRequest,
  ParseContentGenerationItem,
  GenerateContentForSkusRequest,
  ContentLibraryManagementRequest,
  ExportContentRequest,
  ProductKeywordsComparisonRequest,
  ComparisonAnalysis,
  ProductKeywordsPayload,
  ContentMetricType,
  KeywordSection,
  KeywordContent,
  KeywordGroup,
  Section,
  Phrase,
  Keyword,
  GetProductKeywordRequest,
  GetProductKeywordResponse,
  StreamCompareProductKeywordsSummaryRequest,
  ProductAnalysis,
  CreditStatusResponse,
  GetContentLibraryRequest,
  GetContentLibraryResponse,
  GetContentGenerationMetricsRequest,
  GetContentGenerationMetricsResponse,
  ContentGenerationMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import {
  apiGetContentGenerationStatus,
  apiGetCurrentContent,
  apiSaveAsDraft,
  apiSaveAsMaster,
  apiArchiveContent,
  apiGetProfile,
  apiGenerateContentForSkus,
  apiExportContent,
  apiCompareProductKeywords,
  apiGetProductKeywords,
  apiStreamCompareProductKeywordsSummary,
  apiGetCreditStatus,
  apiGetContentLibrary,
  apiGetContentGenerationMetrics
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import { useQuery } from '@tanstack/react-query';
import {
  parseAndMergeNewContent,
  convertExportResponseToCSV
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import { ContentMetricType as LegacyContentMetricType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useState, useCallback, useRef, useEffect } from 'react';
import { AxiosResponse } from 'axios';

const CONTENT_GENERATION_STATUS_KEY = 'ContentGenerationStatus';
const CURRENT_CONTENT_KEY = 'CurrentContent';
const CONTENT_GENERATION_KEY = 'ContentGeneration';

/**
 * Formats keyword sections to ensure proper structure and compatibility
 * Converts the API response to the expected Section format
 */
const formatKeywordSections = (sections: KeywordSection[]): Section[] => {
  if (!sections || !Array.isArray(sections)) return [];

  return sections.map(
    (section) =>
      ({
        name: section.name,
        content: Array.isArray(section.content)
          ? section.content.map(
              (item: KeywordContent) =>
                ({
                  phrase: item.phrase,
                  groupId: item.groupId || undefined
                } as Phrase)
            )
          : [],
        groups: Array.isArray(section.groups)
          ? section.groups.map((group: KeywordGroup) => ({
              groupId: group.groupId,
              weights: Array.isArray(group.weights)
                ? group.weights.map(
                    (weight) =>
                      ({
                        keyword: weight.keyword,
                        is_client_keyword: weight.is_client_keyword
                      } as Keyword)
                  )
                : []
            }))
          : []
      } as Section)
  );
};

type CompareContentRequest = GetCurrentContentRequest & Pick<GetContentGenerationStatusRequest, 'beaconClientId'>;

/**
 * Consolidated hook that fetches current content, generated content, and performs keyword comparison.
 * Waits for both content fetches to complete before performing keyword analysis.
 */
export const useCompareProductKeywords = (requestData: CompareContentRequest) => {
  const { retailerSku, retailerId, weekId, skuContentGenerationId, beaconClientId } = requestData;

  // Fetch current content
  const {
    data: currentContentData,
    isLoading: isLoadingCurrentContent,
    error: errorCurrentContent
  } = useQuery({
    queryKey: [CURRENT_CONTENT_KEY, retailerSku, retailerId, weekId, skuContentGenerationId],
    queryFn: async () => {
      const response = await apiGetCurrentContent({
        retailerSku,
        retailerId,
        weekId,
        skuContentGenerationId
      });
      return {
        currentContent: response.data
      };
    },
    enabled: !!retailerSku && !!retailerId && !!weekId
  });

  const {
    data: generatedContentData,
    isLoading: isLoadingGeneratedContent,
    error: errorGeneratedContent
  } = useQuery({
    queryKey: [CONTENT_GENERATION_STATUS_KEY, retailerSku, retailerId, weekId, skuContentGenerationId],
    queryFn: async () => {
      const newContentsResponse = await apiGetContentGenerationStatus({
        beaconClientId,
        skuContentGenerationIds: [skuContentGenerationId]
      });
      const rawData = newContentsResponse.data;

      const parsedGeneratedContent: Record<string, ParseContentGenerationItem> = {};

      Object.entries(rawData).forEach(([key, contentItem]) => {
        parsedGeneratedContent[key] = parseAndMergeNewContent(contentItem);
      });

      return {
        parsedGeneratedContent
      };
    },
    enabled: !!beaconClientId && !!skuContentGenerationId && !!retailerSku && !!retailerId && !!weekId
  });

  const currentContent = currentContentData?.currentContent;
  const generatedContent = generatedContentData?.parsedGeneratedContent;

  const currentContentForComparison = currentContent
    ? {
        title: currentContent.title,
        description: currentContent.description,
        bullets: currentContent.bullets
      }
    : undefined;

  const generatedContentForComparison = generatedContent?.[skuContentGenerationId]
    ? {
        title: generatedContent[skuContentGenerationId].content.title,
        description: generatedContent[skuContentGenerationId].content.description,
        featureBullets: generatedContent[skuContentGenerationId].content.featureBullets
      }
    : undefined;

  // Perform keyword comparison - only when both content pieces are available
  const {
    data: comparisonData,
    isLoading: isLoadingComparison,
    error: errorComparison
  } = useQuery({
    queryKey: [
      'PRODUCT_KEYWORDS_COMPARISON',
      retailerSku,
      retailerId,
      weekId,
      skuContentGenerationId,
      !!generatedContentForComparison
    ],
    queryFn: async () => {
      // If we have both current and generated content, use comparison API
      if (currentContentForComparison && generatedContentForComparison) {
        // Convert current content to ProductKeywordsPayload format
        const currentPayload: ProductKeywordsPayload = {
          retailerId,
          retailerSku,
          weekId,
          title: currentContentForComparison?.title,
          description: currentContentForComparison?.description,
          bulletPoints: currentContentForComparison?.bullets || [],
          contentMetricType: ContentMetricType.KeywordOrganicTraffic,
          skuContentGenerationId
        };

        // Convert generated content to ProductKeywordsPayload format
        const newPayload: ProductKeywordsPayload = {
          retailerId,
          retailerSku,
          weekId,
          title: generatedContentForComparison?.title,
          description: generatedContentForComparison?.description,
          bulletPoints: generatedContentForComparison?.featureBullets || [],
          contentMetricType: ContentMetricType.KeywordOrganicTraffic,
          skuContentGenerationId
        };

        const comparisonRequest: ProductKeywordsComparisonRequest = {
          current: currentPayload,
          new: newPayload
        };

        const response = await apiCompareProductKeywords(comparisonRequest);
        return {
          type: 'comparison' as const,
          data: response.data.data
        };
      }
      // Fallback: if we only have current content, use old API
      else if (currentContentForComparison) {
        const fallbackRequest: GetProductKeywordRequest = {
          retailerId,
          retailerSku,
          weekId,
          title: currentContentForComparison?.title || '',
          description: currentContentForComparison?.description || '',
          bulletPoints: currentContentForComparison?.bullets || [],
          contentMetricType: LegacyContentMetricType.KeywordOrganicTraffic as string,
          skuContentGenerationId: skuContentGenerationId || ''
        };

        const response = await apiGetProductKeywords(fallbackRequest);
        return {
          type: 'fallback' as const,
          data: response.data
        };
      }

      // No content available
      throw new Error('No content available for keyword analysis');
    },
    enabled:
      !!currentContentForComparison &&
      !!retailerSku &&
      !!retailerId &&
      !!weekId &&
      !isLoadingCurrentContent &&
      !isLoadingGeneratedContent
  });

  // Format keyword data based on which API was used
  let formattedCurrentSections: Section[] | undefined;
  let formattedNewSections: Section[] | undefined;
  if (comparisonData?.type === 'comparison') {
    // New comparison API response
    const comparisonResult = comparisonData.data as ComparisonAnalysis;
    formattedCurrentSections = comparisonResult?.current_analysis?.sections
      ? formatKeywordSections(comparisonResult.current_analysis.sections)
      : undefined;
    formattedNewSections = comparisonResult?.new_analysis?.sections
      ? formatKeywordSections(comparisonResult.new_analysis.sections)
      : undefined;
  } else if (comparisonData?.type === 'fallback') {
    // Old API response (current content only)
    const fallbackResult = comparisonData.data as GetProductKeywordResponse;
    if (fallbackResult.isSuccess && fallbackResult.data?.weightedKeywordPhrases?.sections) {
      formattedCurrentSections = fallbackResult.data.weightedKeywordPhrases.sections;
    }
    // No generated content sections in fallback mode
    formattedNewSections = undefined;
  }

  // Combine all loading states and errors
  const isLoading = isLoadingCurrentContent || isLoadingGeneratedContent || isLoadingComparison;
  const error = errorCurrentContent || errorGeneratedContent || errorComparison;

  return {
    currentContent,
    generatedContent,
    comparisonData,
    formattedCurrentSections,
    formattedNewSections,
    isLoading,
    error,
    isLoadingCurrentContent,
    isLoadingGeneratedContent,
    isLoadingComparison
  };
};

export type ContentLibraryAction = 'draft' | 'master' | 'archive';

export const useUpdateContentLibrary = (type: ContentLibraryAction) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const getApiFunction = (actionType: ContentLibraryAction) => {
    switch (actionType) {
      case 'draft':
        return apiSaveAsDraft;
      case 'master':
        return apiSaveAsMaster;
      case 'archive':
        return apiArchiveContent;
      default:
        throw new Error(`Unsupported content library action: ${actionType}`);
    }
  };

  const getSuccessMessage = (actionType: ContentLibraryAction) => {
    switch (actionType) {
      case 'draft':
        return "You've successfully saved the generated content for this product to the library.";
      case 'master':
        return 'You’ve successfully saved the generated content for this product as the master.';
      case 'archive':
        return "You've successfully archived the generated content for this product.";
      default:
        return 'Operation completed successfully';
    }
  };

  const getErrorMessage = (actionType: ContentLibraryAction) => {
    switch (actionType) {
      case 'draft':
        return 'Unable to mark the generated content as reviewed';
      case 'master':
        return 'Unable to save content as master';
      case 'archive':
        return 'Unable to archive content';
      default:
        return 'Operation failed';
    }
  };

  const { mutate, mutateAsync, isPending, error, isSuccess, data } = useMutation({
    mutationFn: async (requestData: ContentLibraryManagementRequest) => {
      const apiFunction = getApiFunction(type);
      const response = await apiFunction(requestData);
      return response.data;
    },
    onSuccess: (response, variables) => {
      // Invalidate all content generation status queries since we only have skuContentGenerationId
      queryClient.invalidateQueries({
        queryKey: [CONTENT_GENERATION_STATUS_KEY]
      });

      showSnackbar({
        message: getSuccessMessage(type),
        type: 'success'
      });

      return response;
    },
    onError: (error) => {
      const rawErrorMessage = error instanceof Error ? error.message : getErrorMessage(type);
      console.error(`Content library ${type} error:`, rawErrorMessage);

      // Show user-friendly error message
      showSnackbar({
        message: 'An error occurred. Please reach out to support@stackline.com for further guidance.',
        type: 'error'
      });
    }
  });

  return {
    updateContentLibrary: mutate,
    updateContentLibraryAsync: mutateAsync,
    isPending,
    error,
    isSuccess,
    data
  };
};

export const useGetContentGenerationProfile = (skuContentGenerationId: string) => {
  const beaconClientId = useAppSelector((state) => state.user.config?.vendor?.BeaconClientId);
  const {
    data: contentGenData,
    isLoading: isLoadingContentGen,
    error: contentGenError
  } = useQuery({
    queryKey: [CONTENT_GENERATION_STATUS_KEY, skuContentGenerationId],
    queryFn: async () => {
      if (!beaconClientId) {
        throw new Error('Missing beaconClientId');
      }

      const response = await apiGetContentGenerationStatus({
        beaconClientId,
        skuContentGenerationIds: [skuContentGenerationId]
      });

      return response;
    },
    enabled: !!skuContentGenerationId && !!beaconClientId
  });

  const contentGenItem = contentGenData?.data?.[skuContentGenerationId];
  const profileId = contentGenItem?.profileId;
  const profileVersion = contentGenItem?.profileVersion;

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    error: profileError
  } = useQuery({
    queryKey: ['Profile', profileId, profileVersion],
    queryFn: async () => {
      const response = await apiGetProfile({
        profileId,
        version: profileVersion
      });

      return response.data;
    },
    enabled: !!profileId && profileId.length > 0
  });

  const isLoading = isLoadingContentGen || isLoadingProfile;
  const error = contentGenError || profileError;

  return {
    contentGenerationProfile: profileData,
    contentGeneration: contentGenItem,
    isLoading,
    error
  };
};

export const useGenerateContentForSkus = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const userEmail = useAppSelector((state) => state.user.config?.profile?.email);
  const beaconClientId = useAppSelector((state) => state.user.config?.vendor?.BeaconClientId);

  const { mutate, isPending, error, isSuccess, data } = useMutation({
    mutationFn: async (requestData: GenerateContentForSkusRequest) => {
      const contentResponse = await apiGenerateContentForSkus(requestData);
      return contentResponse.data;
    },
    onSuccess: (response, variables) => {
      // Track content generation in Mixpanel
      trackGenerationStarted({
        skuCount: variables.contentRequests?.length || 0,
        profileId: variables.contentRequests?.[0]?.profileId
      });

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: [CONTENT_GENERATION_KEY]
      });

      // Invalidate credit status to reflect updated balance after generation
      queryClient.invalidateQueries({
        queryKey: ['CreditStatus']
      });
    },
    onError: (error) => {
      let errorMessage = 'Failed to start content generation';

      // Check if it's a 400 error (insufficient credits)
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as any).response;
        if (response?.status === 400) {
          errorMessage = 'Not enough credits';
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      console.error('Generate content error:', errorMessage);

      showSnackbar({
        message: errorMessage,
        type: 'error'
      });
    }
  });

  return {
    generateContentForSkus: mutate,
    isPending,
    error,
    isSuccess,
    data
  };
};

export const useExportContent = () => {
  const { showSnackbar } = useSnackbar();

  const { mutate, isPending, error, isSuccess, data } = useMutation({
    mutationFn: async (requestData: ExportContentRequest) => {
      const response = await apiExportContent(requestData);
      return response.data;
    },
    onSuccess: (response) => {
      // Convert response to CSV and trigger download
      convertExportResponseToCSV(response.data, (message, type) => showSnackbar({ message, type }));
      return response;
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export content';
      console.error('Export content error:', errorMessage);

      showSnackbar({
        message: errorMessage,
        type: 'error'
      });
    }
  });

  return {
    exportContent: mutate,
    isPending,
    error,
    isSuccess,
    data
  };
};

/**
 * Hook for fetching content library data
 */
export const useGetContentLibrary = (request: GetContentLibraryRequest, options?: { enabled?: boolean }) => {
  const { enabled = true } = options ?? {};

  const { data, isLoading, error, refetch } = useQuery<GetContentLibraryResponse>({
    queryKey: ['ContentLibrary', request],
    queryFn: async () => {
      const response = await apiGetContentLibrary(request);
      return response.data;
    },
    staleTime: 0, // Allow immediate refetch when invalidated
    refetchOnWindowFocus: false,
    enabled
  });

  return {
    data,
    isLoading,
    error,
    refetch
  };
};

/**
 * Hook for streaming the comparison summary with proper line-by-line parsing and retry logic
 */
export const useStreamComparisonSummary = () => {
  const [summary, setSummary] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamError, setStreamError] = useState<Error | null>(null);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const processStreamLine = useCallback((line: string, currentSummary: string): string => {
    if (!line.trim()) return currentSummary;

    // Handle SSE format: "data: content"
    if (line.startsWith('data: ')) {
      const content = line.substring(6); // Remove "data: " prefix
      return currentSummary + content;
    }

    // Handle other SSE events (like "event:", "id:", etc.) - ignore them
    if (line.includes(': ')) {
      return currentSummary;
    }

    try {
      // Try to parse as JSON first
      const jsonData = JSON.parse(line);
      // If it's JSON, you might want to handle it differently
      // For now, just append it as formatted text
      return currentSummary + JSON.stringify(jsonData, null, 2) + '\n';
    } catch {
      // Not JSON, treat as plain text
      return currentSummary + line;
    }
  }, []);

  const streamSummaryInternal = useCallback(
    async (requestBody: StreamCompareProductKeywordsSummaryRequest): Promise<void> => {
      try {
        // Create new abort controller for this request
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        const stream = await apiStreamCompareProductKeywordsSummary(requestBody, abortControllerRef.current.signal);

        const reader = stream.getReader();
        const decoder = new TextDecoder();

        let buffer = '';
        let accumulatedSummary = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Process any remaining buffer
            if (buffer.trim()) {
              accumulatedSummary = processStreamLine(buffer, accumulatedSummary);
              setSummary(accumulatedSummary);
            }
            setIsComplete(true);
            break;
          }

          // Check if request was aborted
          if (abortControllerRef.current?.signal.aborted) {
            reader.cancel();
            return;
          }

          // Decode chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines (separated by \n)
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          // Process each complete line
          for (const line of lines) {
            accumulatedSummary = processStreamLine(line, accumulatedSummary);
          }

          setSummary(accumulatedSummary);
        }
      } catch (error) {
        const streamErr = error instanceof Error ? error : new Error('Streaming failed');

        // Don't set error if aborted (user cancelled)
        if (!abortControllerRef.current?.signal.aborted) {
          setStreamError(streamErr);
          console.error('Summary streaming failed:', streamErr);
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [processStreamLine]
  );

  const streamSummary = useCallback(
    async (
      currentAnalysis: any,
      newAnalysis: any,
      currentContent?: { title?: string; description?: string; bullets?: string[] },
      newContent?: { title?: string; description?: string; featureBullets?: string[] }
    ) => {
      // Prevent concurrent streaming attempts
      if (isStreaming) {
        console.warn('Streaming already in progress, ignoring new request');
        return;
      }

      // Reset state
      setSummary('');
      setStreamError(null);
      setIsStreaming(true);
      setIsComplete(false);

      const requestBody: StreamCompareProductKeywordsSummaryRequest = {
        current_analysis: currentAnalysis,
        new_analysis: newAnalysis,
        // Include optional content if provided
        ...(currentContent && {
          current: {
            title: currentContent.title || '',
            description: currentContent.description || '',
            bullet_points: currentContent.bullets || []
          }
        }),
        ...(newContent && {
          new: {
            title: newContent.title || '',
            description: newContent.description || '',
            bullet_points: newContent.featureBullets || []
          }
        })
      };

      await streamSummaryInternal(requestBody);
    },
    [isStreaming, streamSummaryInternal]
  );

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    setStreamError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    summary,
    isStreaming,
    streamError,
    isComplete,
    streamSummary,
    cancelStream
  };
};

/**
 * Hook for fetching the current credit status for content generation
 * Fetches once when the component renders
 */
export const useCreditStatus = () => {
  const appName = getAppName();
  const isAtlas = appName === AppName.Atlas;

  const {
    data: creditStatusData,
    isLoading: isLoadingCreditStatus,
    error: creditStatusError,
    refetch: refetchCreditStatus
  } = useQuery({
    queryKey: ['CreditStatus'],
    queryFn: async () => {
      const response = await apiGetCreditStatus();
      return response;
    },
    enabled: !isAtlas
  });

  const creditBalance = creditStatusData?.data?.creditsAvailable || 0;
  const creditsInUse = creditStatusData?.data?.creditsInUse || 0;
  const creditsUsed = creditStatusData?.data?.creditsUsed || 0;
  const isError = !!creditStatusError;

  return {
    creditBalance,
    creditsInUse,
    creditsUsed,
    isLoading: isLoadingCreditStatus,
    isError,
    refetchCreditStatus,
    rawData: creditStatusData
  };
};

/**
 * Hook for fetching content generation metrics for a specific content version
 * Can be used to score both current content and generated content
 */
export const useGetContentGenerationMetrics = (
  request: GetContentGenerationMetricsRequest,
  enabled: boolean = true
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ContentGenerationMetrics', request],
    queryFn: async () => {
      const response = await apiGetContentGenerationMetrics(request);
      return response.data;
    },
    enabled: enabled && !!request.retailerSku && !!request.retailerId && !!request.weekId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  return {
    metrics: data as GetContentGenerationMetricsResponse['data'],
    isLoading,
    error,
    refetch
  };
};
